import { useState } from 'react';
import {
  Card,
  Layout,
  Text,
  Button,
  Box,
  Toast,
  Frame,
  Banner,
} from '@shopify/polaris';

function ImportExport() {
  const [loading, setLoading] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [importData, setImportData] = useState<string>('');

  const API_URL = 'https://ts-team-shopify.anonnaabir.workers.dev';

  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/import-export/export`);
      const result = await response.json();
      if (result.success) {
        const blob = new Blob([JSON.stringify(result.data, null, 2)], {
          type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ts-team-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setToastMessage('Data exported successfully');
        setToastActive(true);
      } else {
        setToastMessage(result.error || 'Failed to export data');
        setToastActive(true);
      }
    } catch (error) {
      setToastMessage('Error exporting data');
      setToastActive(true);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      setToastMessage('Please paste your import data');
      setToastActive(true);
      return;
    }

    setLoading(true);
    try {
      const data = JSON.parse(importData);
      const response = await fetch(`${API_URL}/api/import-export/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (result.success) {
        const { teamMembers, teamShowcases, categories } = result.data;
        setToastMessage(
          `Import complete: ${teamMembers.created} members, ${teamShowcases.created} showcases, ${categories.created} categories created`
        );
        setToastActive(true);
        setImportData('');
      } else {
        setToastMessage(result.error || 'Failed to import data');
        setToastActive(true);
      }
    } catch (error) {
      setToastMessage('Invalid JSON data');
      setToastActive(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setImportData(content);
      };
      reader.readAsText(file);
    }
  };

  const toastMarkup = toastActive ? (
    <Toast
      content={toastMessage}
      onDismiss={() => setToastActive(false)}
    />
  ) : null;

  return (
    <Frame>
      <Layout>
        <Layout.Section>
          <Card>
            <Box padding="400">
              <Text variant="headingMd" as="h2">
                Import / Export
              </Text>
              <Box paddingBlockStart="100">
                <Text variant="bodyMd" as="p">
                  Export your team data to backup or transfer between stores.
                </Text>
              </Box>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
            <Card>
              <Box padding="400">
                <Text variant="headingSm" as="h3">
                  Export Data
                </Text>
                <Box paddingBlockStart="100">
                  <Text variant="bodyMd" as="p">
                    Download all your team members, showcases, and categories as a JSON file.
                  </Text>
                </Box>
                <Box paddingBlockStart="300">
                  <Button
                    variant="primary"
                    onClick={handleExport}
                    loading={loading}
                  >
                    Export All Data
                  </Button>
                </Box>
              </Box>
            </Card>

            <Card>
              <Box padding="400">
                <Text variant="headingSm" as="h3">
                  Import Data
                </Text>
                <Box paddingBlockStart="100">
                  <Text variant="bodyMd" as="p">
                    Import team data from a previously exported JSON file.
                  </Text>
                </Box>
                <Box paddingBlockStart="300">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileUpload}
                    style={{ marginBottom: '12px' }}
                  />
                  <textarea
                    value={importData}
                    onChange={(e) => setImportData(e.target.value)}
                    placeholder="Or paste your JSON data here..."
                    style={{
                      width: '100%',
                      minHeight: '150px',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                    }}
                  />
                  <Box paddingBlockStart="200">
                    <Button
                      variant="primary"
                      onClick={handleImport}
                      disabled={!importData.trim()}
                      loading={loading}
                    >
                      Import Data
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Card>
          </div>
        </Layout.Section>

        <Layout.Section>
          <Banner>
            <Text variant="bodyMd" as="p">
              <strong>Note:</strong> Importing data will add new items without affecting existing data.
              Duplicate items may be created if you import the same data multiple times.
            </Text>
          </Banner>
        </Layout.Section>
      </Layout>

      {toastMarkup}
    </Frame>
  );
}

export default ImportExport;
